import type { QueryClient } from "@tanstack/react-query";

/**
 * Re-sync the React Query cache to a freshly-authenticated session.
 *
 * Sign-in / sign-up swaps the session cookie server-side, but the in-memory
 * QueryClient still holds the *previous* identity's tenant-scoped queries
 * (home, notifications, people, …). Navigating with those around fires
 * requests with stale workspace ids, the server 404s (tenant isolation) and
 * the user sees a broken page until a full refresh rebuilds the cache.
 * Sign-out already drops everything via `qc.clear()`; this is its sign-in
 * counterpart and must be called at every point we acquire a new session.
 *
 * Order matters:
 *   1. Refetch `auth/me`, its observer is mounted app-wide (AuthProvider), so
 *      invalidating refetches and updates it in place to the new session.
 *   2. Remove every *other* cached query so no prior-identity data survives.
 *      At sign-in time these are all inactive (we're on an auth-layout route
 *      with no app shell), so this just drops stale cache, the new session's
 *      queries refetch fresh once the shell mounts.
 */
export async function applyNewSession(queryClient: QueryClient): Promise<void> {
  await queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
  queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== "auth" });
}
